import React from 'react';
import PropTypes from 'prop-types';
import './MainSidebar.css';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import { useSelector } from 'react-redux';
import { faCheckSquare, faTruck } from '@fortawesome/pro-solid-svg-icons';
import { Badge } from 'react-bootstrap';
import MainSidebarItem from '../MainSidebarItem/MainSidebarItem';
import MainSidebarItemGroup from '../MainSidebarItemGroup/MainSidebarItemGroup';
import { RaaRoles, sessionUserHasRolePrivileges } from '../../../utility/SecurityRules';
import SubscriptionSelectDropDown from './SubscriptionSelectDropDown';
import HasRolePrivileges from '../../highorder/HasRolePrivileges';
import {
  selectActiveSubscriptionCounts,
  selectSessionInformation,
  selectUserAssignmentPreferences,
  subscriptionPropType,
} from '../../../redux/slices/sessionSlice';
import ChecklistCompletionBadge from '../../setup/ChecklistCompletionBadge';

export const SidebarSections = {
  DASHBOARD: 'dashboard',
  DEVICES: 'devices',
  MONITOR360: 'monitor360',
  ALERTS: 'alerts',
  ACTIONS: 'actions',
  SENSOR_DATA: 'sensorData',
  GUIDANCE: 'guidance',
  ORDERS: 'orders',
  DOWNLOADS: 'downloads',
  SETUP_CHECKLIST: 'setupChecklist',
};

function MainSidebar(props) {
  const {
    toggleSidebar,
    isMinified,
    userAssignmentList,
    onActiveSubscriptionChange,
    alertCount,
    disabledSections,
  } = props;

  const isDisabled = (section) => disabledSections.includes(section);

  /* istanbul ignore next */
  const { activeSubscription } = useSelector(selectSessionInformation);
  const { primaryChecklistHidden } = useSelector(selectUserAssignmentPreferences);

  const {
    transferredDeviceCount,
    archivedDeviceCount,
    legacyDeviceCount,
  } = useSelector(selectActiveSubscriptionCounts);

  const alertBadge = alertCount > 0 ? (
    <span className="pl-3">
      <Badge
        pill
        style={{
          backgroundColor: '#EE3524',
          padding: '3px 7px',
          fontSize: '12px',
        }}
        className="checklist-completion-badge text-white font-weight-bold"
      >
        {alertCount}
      </Badge>
    </span>
  ) : null;

  const checklistBadge = (
    <span className="pl-3">
      <ChecklistCompletionBadge />
    </span>
  );

  /* istanbul ignore next */
  return (
    <aside id="left-panel">
      <SubscriptionSelectDropDown
        userAssignmentList={userAssignmentList}
        onActiveSubscriptionChange={onActiveSubscriptionChange}
        activeSubscription={activeSubscription}
      />

      <nav>
        <ul>
          {sessionUserHasRolePrivileges(
            activeSubscription,
            [RaaRoles.ADMIN, RaaRoles.MANAGER, RaaRoles.VIEWER, RaaRoles.BILLING],
          ) && (
            <>
              <HasRolePrivileges
                targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER, RaaRoles.VIEWER]}
              >
                <MainSidebarItemGroup
                  icon="gauge-high"
                  label="Dashboard"
                  to="/s/dashboard"
                  matcher="/s/dashboard"
                  disabled={isDisabled(SidebarSections.DASHBOARD)}
                />

                <MainSidebarItemGroup
                  icon="list"
                  label="Devices"
                  to="/s/devices"
                  matcher="/s/devices"
                  disabled={isDisabled(SidebarSections.DEVICES)}
                >
                  <MainSidebarItem to="/s/devices" exact>Inventory</MainSidebarItem>
                  <li>
                    <a href="/devices/device-map">
                      <span className="menu-item-parent">Map</span>
                      <span />
                    </a>
                  </li>
                  <li>
                    <a href="/links">
                      <span className="menu-item-parent">Room Alert Links</span>
                      <span />
                    </a>
                  </li>
                  {transferredDeviceCount !== 0 && (
                    <li>
                      <a href="/devices/transfers">
                        <span className="menu-item-parent">Transfers</span>
                        <span />
                      </a>
                    </li>
                  )}
                  {archivedDeviceCount !== 0 && (
                    <li>
                      <a href="/devices/archived">
                        <span className="menu-item-parent">Archived</span>
                        <span />
                      </a>
                    </li>
                  )}
                  {legacyDeviceCount !== 0 && (
                    <li>
                      <a href="/devices/legacy">
                        <span className="menu-item-parent">Legacy</span>
                        <span />
                      </a>
                    </li>
                  )}
                  <MainSidebarItem to="/s/devices/saved-searches" exact>Searches</MainSidebarItem>
                </MainSidebarItemGroup>

                <MainSidebarItemGroup
                  icon="code-branch"
                  label="Monitor360"
                  to="/sensors/monitor360"
                  matcher="/sensors/monitor360"
                  disabled={isDisabled(SidebarSections.MONITOR360)}
                />

                <MainSidebarItemGroup
                  icon="bolt"
                  label="Alerts"
                  to="/s/alerts"
                  matcher="/s/alerts"
                  disabled={isDisabled(SidebarSections.ALERTS)}
                  badge={alertBadge}
                >
                  <MainSidebarItem to="/s/alerts" exact>List</MainSidebarItem>
                  <MainSidebarItem to="/s/alerts/history" exact>History</MainSidebarItem>
                  <MainSidebarItem to="/s/alerts/saved-searches" exact>Searches</MainSidebarItem>
                </MainSidebarItemGroup>

                <MainSidebarItemGroup
                  icon="circle-play"
                  label="Actions"
                  to="/s/actions/notification-groups"
                  matcher="/s/actions"
                  disabled={isDisabled(SidebarSections.ACTIONS)}
                >
                  <MainSidebarItem to="/s/actions/notification-groups">Notification Groups</MainSidebarItem>
                  <MainSidebarItem to="/s/actions/quiet-times">Quiet Times</MainSidebarItem>
                  <MainSidebarItem to="/s/actions/notification-templates">Notification Templates</MainSidebarItem>
                </MainSidebarItemGroup>

                <MainSidebarItemGroup
                  icon="chart-area"
                  label="Sensor Data & Reports"
                  to="/s/sensor-data/groups"
                  matcher="/s/sensor-data/"
                  disabled={isDisabled(SidebarSections.SENSOR_DATA)}
                >
                  <MainSidebarItem to="/s/sensor-data/explore">Explore</MainSidebarItem>
                  <MainSidebarItem to="/s/sensor-data/groups">Data Groups</MainSidebarItem>
                </MainSidebarItemGroup>

                <MainSidebarItemGroup
                  icon="shield"
                  label="Guidance"
                  to="/s/guidance/threshold-ranges"
                  matcher="/s/guidance"
                  disabled={isDisabled(SidebarSections.GUIDANCE)}
                >
                  <MainSidebarItem to="/s/guidance/threshold-ranges">Threshold Ranges</MainSidebarItem>
                  <MainSidebarItem to="/s/guidance/established">Established Standards</MainSidebarItem>
                  <MainSidebarItem to="/s/guidance/tags">Tags</MainSidebarItem>
                </MainSidebarItemGroup>
              </HasRolePrivileges>

              {sessionUserHasRolePrivileges(activeSubscription,
                [RaaRoles.ADMIN, RaaRoles.BILLING]) && (
                <MainSidebarItemGroup
                  icon={faTruck}
                  label="Orders"
                  to="/s/orders"
                  matcher="/s/orders/"
                  disabled={isDisabled(SidebarSections.ORDERS)}
                />
              )}
            </>
          )}

          {sessionUserHasRolePrivileges(activeSubscription, [RaaRoles.ADMIN, RaaRoles.MANAGER]) && (
            <MainSidebarItemGroup
              icon="download"
              label="Downloads"
              to="/downloads"
              matcher="/downloads"
              disabled={isDisabled(SidebarSections.DOWNLOADS)}
            />
          )}

          {sessionUserHasRolePrivileges(activeSubscription, [RaaRoles.ADMIN])
            && !primaryChecklistHidden && (
            <MainSidebarItemGroup
              icon={faCheckSquare}
              label="Setup Checklist"
              to="/s/setup/checklist"
              matcher="/s/setup/checklist"
              disabled={isDisabled(SidebarSections.SETUP_CHECKLIST)}
              badge={checklistBadge}
            />
          )}
        </ul>
      </nav>

      <span className="minifyme" onClick={toggleSidebar}>
        <Icon
          icon={isMinified ? 'circle-arrow-right' : 'circle-arrow-left'}
          className="fa-fw"
          size="lg"
        />
      </span>
    </aside>
  );
}

MainSidebar.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
  isMinified: PropTypes.bool.isRequired,
  onActiveSubscriptionChange: PropTypes.func.isRequired,
  userAssignmentList: PropTypes.arrayOf(subscriptionPropType.isRequired).isRequired,
  alertCount: PropTypes.number.isRequired,
  disabledSections: PropTypes.arrayOf(PropTypes.string),
};

MainSidebar.defaultProps = {
  disabledSections: [],
};

export { MainSidebar };

export default MainSidebar;
